import { Check, Copy, MailCheck, RefreshCw } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { useCopyToClipboard } from '@reflex/ui/hooks/useCopyToClipboard';
import type { InviteShare } from './inviteShare';

/**
 * The one-time redeem links, one row per address. Rendered inside whichever
 * dialog minted them — the Add member dialog swaps its own body to this after
 * a send, rather than opening a second dialog on top of itself.
 */
export function ShareInviteList({ shares }: { shares: InviteShare[] }) {
  return (
    <ul className="flex flex-col gap-3">
      {shares.map((share) => (
        <ShareInviteRow key={share.invite.id} share={share} />
      ))}
    </ul>
  );
}

function ShareInviteRow({ share }: { share: InviteShare }) {
  const { copied, copy } = useCopyToClipboard();
  return (
    <li className="flex flex-col gap-1" data-testid={`invite-share-${share.invite.id}`}>
      <p className="truncate text-xs font-medium text-muted-foreground">{share.invite.email}</p>
      {share.emailSent && (
        <p
          className="flex items-center gap-1 text-xs text-status-success"
          data-testid="invite-share-emailed"
        >
          <MailCheck className="icon-sm" aria-hidden="true" />
          Email sent. Ask them to check their inbox.
        </p>
      )}
      {share.reissued && (
        <p
          className="flex items-center gap-1 text-xs text-status-warning"
          data-testid="invite-share-reissued"
        >
          <RefreshCw className="icon-sm" aria-hidden="true" />
          Invite reissued. The previous link has been invalidated and no longer works.
        </p>
      )}
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={share.redeemUrl}
          aria-label={`Invite link for ${share.invite.email}`}
          data-testid="invite-share-url"
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => copy(share.redeemUrl)}
          aria-label={`Copy invite link for ${share.invite.email}`}
        >
          {copied ? <Check className="icon-sm" /> : <Copy className="icon-sm" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </li>
  );
}
